import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TemplateVisitPresetDto, TrialVisitContact } from '@ctms/contracts';
import { Building2, Home, Lock, Phone, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../../api/client.js';
import { patientsApi } from '../../patients.api.js';
import { patientQueryKeys } from '../../patients.query-keys.js';

/**
 * #41 „Wizyty szablonowe" — the non-protocol preset window (frozen spec
 * `spec/features/patients-pathways/template-visits.feature`, revised per the
 * #43/#42 decisions). The preset set (names / codes / channels / descriptions
 * / repeatability) is SERVER-SIDE CONFIGURATION (GO conclusion 3) — this
 * component renders whatever the dialog-state door returns and never
 * hardcodes category semantics beyond the contract.
 *
 * Frozen rules implemented here:
 * - every ACTIVE checkbox is checked by DEFAULT; unchecking adds nothing yet;
 * - a ONE-TIME preset (category OUT) already on the schedule renders with an
 *   INACTIVE, unchecked checkbox (the server backs it with 409);
 * - a REPEATABLE preset (UNS) stays ACTIVE even when already added — the note
 *   is purely informational (#43: „tyle razy, ile potrzeba");
 * - „DODAJ WIZYTY" sends ONE all-or-nothing bulk of the checked presets in
 *   CONFIGURED order; a failure (e.g. 409 TEMPLATE_VISIT_DUPLICATE) keeps the
 *   dialog open with the checks preserved for a retry — nothing was saved;
 * - a successful bulk closes the dialog; the parent invalidates the schedule
 *   and the change log (preset visits carry no booked day).
 */
export interface TemplateVisitsDialogProps {
  /** The chart owning the participation (`patients.id`). */
  patientId: number;
  /** The participation whose schedule receives the presets (`trial_enrollments.id`). */
  enrollmentId: number;
  /** Presentation name of the patient (e.g. `Anna Testowa`). */
  patientName: string;
  /** Protocol number of the active trial (e.g. `PROTOCOL-2026-A`). */
  trialLabel: string;
  /** Called after a successful bulk — the parent invalidates schedule/log caches. */
  onAdded: () => void;
  onClose: () => void;
}

const CONTACT_ICONS: Record<TrialVisitContact, typeof Building2> = {
  site: Building2,
  home: Home,
  phone: Phone,
  site_imp: Lock,
};

const CONTACT_LABELS: Record<TrialVisitContact, string> = {
  site: 'Ośrodek',
  home: 'Dom',
  phone: 'Telefon',
  site_imp: 'Ośrodek – wyłącznie podanie leku',
};

/** Legacy channel of a preset row (S/P/H/I) → the catalog contact vocab. */
const LEGACY_CONTACT: Record<TemplateVisitPresetDto['type'], TrialVisitContact> = {
  S: 'site',
  P: 'phone',
  H: 'home',
  I: 'site_imp',
};

/**
 * A preset is SELECTABLE when its category repeats (UNS — `alreadyAdded` is
 * informational only) or when the one-time row is not on the schedule yet.
 * Keyed off the contract flags, never off names/codes (GO 3).
 */
function selectable(preset: TemplateVisitPresetDto): boolean {
  return preset.repeatable || !preset.alreadyAdded;
}

export function TemplateVisitsDialog({
  patientId,
  enrollmentId,
  patientName,
  trialLabel,
  onAdded,
  onClose,
}: TemplateVisitsDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  /** `null` = not initialized yet; the default (all active checked) lands with the data. */
  const [checked, setChecked] = useState<ReadonlySet<string> | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dialogQuery = useQuery({
    queryKey: patientQueryKeys.templateVisits(patientId, enrollmentId),
    queryFn: () => patientsApi.getTemplateVisitsDialog(patientId, enrollmentId),
    // Per-enrollment live state (`alreadyAdded` must be fresh on every open).
    staleTime: 0,
  });
  const items = dialogQuery.data?.items ?? [];

  // Reconcile the selection on EVERY dialog-data change, not only the first one:
  // a reopen against a stale React Query cache initializes `checked` from the old
  // snapshot, then the mount refetch can flip a one-time preset to already-added.
  // Pruning `checked` to the currently selectable names keeps such presets
  // INACTIVE and UNCHECKED (frozen spec) instead of disabled+checked and counted.
  useEffect(() => {
    if (!dialogQuery.data) return;
    const selectableNames = new Set(
      dialogQuery.data.items.filter(selectable).map((preset) => preset.name),
    );
    setChecked((previous) => {
      const base = previous ?? selectableNames;
      const next = new Set([...base].filter((name) => selectableNames.has(name)));
      // Keep the previous reference when nothing changed so React bails out.
      return next.size === base.size ? base : next;
    });
  }, [dialogQuery.data]);

  const addMutation = useMutation({
    mutationFn: (presetNames: string[]) =>
      patientsApi.addTemplateVisits(patientId, enrollmentId, { presetNames }),
    onSuccess: () => {
      setErrorMessage(null);
      // The next open must see the fresh `alreadyAdded` flags.
      void queryClient.invalidateQueries({
        queryKey: patientQueryKeys.templateVisits(patientId, enrollmentId),
      });
      onAdded();
      onClose();
    },
    onError: async (error: unknown) => {
      setErrorMessage(
        await getApiErrorMessage(error, 'Nie udało się dodać wizyt szablonowych.'),
      );
    },
  });

  const pending = addMutation.isPending;
  /** CONFIGURED order (the server preserves it in the response), not click order. */
  // Defense in depth: even in the transitional frame before the reconcile effect
  // runs, a non-selectable preset is never counted nor sent in the bulk.
  const selectedNames = items
    .filter((preset) => selectable(preset) && (checked?.has(preset.name) ?? false))
    .map((preset) => preset.name);

  function toggle(name: string): void {
    setChecked((previous) => {
      const next = new Set(previous ?? []);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-visits-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="template-visits-title" className="text-sm font-semibold text-slate-100">
              Wizyty szablonowe
            </h3>
            <p className="mt-1 text-xs text-slate-400">
              {patientName} · badanie {trialLabel}. Zaznacz presety i dodaj je jednym
              zbiorczym zapisem na harmonogram udziału.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij okno wizyt szablonowych"
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-3 rounded-md border border-cyan-900/60 bg-cyan-950/40 px-3 py-2 text-xs text-cyan-200">
          Wizyty szablonowe nie są uwzględnione w protokole badania — trafiają wyłącznie
          na harmonogram udziału pacjenta, bez płatności i bez okien czasowych.
        </p>

        {dialogQuery.isError ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-rose-900/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200"
          >
            Nie udało się pobrać zestawu wizyt szablonowych. Zamknij okno i spróbuj
            ponownie.
          </p>
        ) : null}

        {errorMessage !== null ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-rose-900/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200"
          >
            {errorMessage} Nic nie zostało zapisane — zaznaczenie pozostało, możesz
            spróbować ponownie.
          </p>
        ) : null}

        {dialogQuery.isLoading ? (
          <p className="mt-4 text-xs text-slate-400">Ładowanie zestawu presetów…</p>
        ) : null}

        {dialogQuery.data ? (
          <div className="mt-4 min-h-0 flex-1 overflow-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] tracking-wide text-slate-500">
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    <span className="sr-only">Zaznacz</span>
                  </th>
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    Wizyta
                  </th>
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    Kod
                  </th>
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    Typ kontaktu
                  </th>
                  <th scope="col" className="px-2 py-1.5 font-medium">
                    Opis
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((preset) => {
                  const contact = LEGACY_CONTACT[preset.type];
                  const Icon = CONTACT_ICONS[contact];
                  const oneTimeAdded = !preset.repeatable && preset.alreadyAdded;
                  return (
                    <tr
                      key={preset.name}
                      className="border-b border-slate-800/60 text-slate-300"
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`Zaznacz ${preset.name}`}
                          className="cursor-pointer accent-emerald-500"
                          checked={checked?.has(preset.name) ?? false}
                          disabled={pending || !selectable(preset)}
                          onChange={() => toggle(preset.name)}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-slate-100">
                        <span className="flex items-center gap-2">
                          {preset.name}
                          {oneTimeAdded ? (
                            <span className="rounded border border-amber-800/70 bg-amber-950/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                              Już dodano — preset jednorazowy
                            </span>
                          ) : null}
                          {preset.repeatable && preset.alreadyAdded ? (
                            <span className="rounded border border-slate-700 bg-slate-800/60 px-1.5 py-0.5 text-[10px] font-medium text-slate-300">
                              Dodano wcześniej — można dodać ponownie
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-400">{preset.code}</td>
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-1.5 text-slate-400">
                          <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                          {CONTACT_LABELS[contact]}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-400">{preset.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-md border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ZAMKNIJ
          </button>
          <button
            type="button"
            disabled={pending || !dialogQuery.data || selectedNames.length === 0}
            onClick={() => addMutation.mutate(selectedNames)}
            className="rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            DODAJ WIZYTY{selectedNames.length > 0 ? ` (${selectedNames.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
